/**
 * FlowsLibrary — the Flows tab of the Messaging rail. Verbatim port of the
 * prototype's FlowsLibraryScreen: communications flows (multi-step journeys
 * built from the message library), searchable + filterable by event type and
 * trigger, opening full-screen in the flow builder.
 *
 * Route: `messaging.flows` · rail tab: Flows.
 */
import { useState } from "react";
import { Btn, Card, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useFlows, flowTrigLabel, FLOW_KIND_PILL, flowStats, type SeedFlow } from "./useFlows";
import { useMessaging } from "./useMessaging";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import { useT } from "../../lib/i18n";

const msgTh = { textAlign: "left" as const, padding: "10px 16px", whiteSpace: "nowrap" as const };
const msgTd = { padding: "12px 16px", verticalAlign: "middle" as const };
const flowSelectStyle: React.CSSProperties = { height: 38, padding: "0 30px 0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, cursor: "pointer" };

export default function FlowsLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useFlows();
  useMessaging(); // warm the messages cache so the rail tab count is live
  const counts = useMessagingCounts();
  const canWrite = can("flows.write");
  const [view, setView] = useViewMode("commflows:view");
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [trigFilter, setTrigFilter] = useState("all");

  const all = store.all();
  const kindOpts = Array.from(new Set(all.map((f) => f.eventKind))).filter(Boolean);
  const trigOpts = Array.from(new Set(all.map((f) => f.trigger && f.trigger.event))).filter(Boolean);
  const rows = all.filter((f) => {
    if (kindFilter !== "all" && f.eventKind !== kindFilter) return false;
    if (trigFilter !== "all" && (f.trigger && f.trigger.event) !== trigFilter) return false;
    if (q.trim() && !f.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const dup = async (f: SeedFlow) => { try { const c = await store.duplicate(f.id); toast({ tone: "success", title: t("messaging.flowsLibrary.flowDuplicated", "Flow duplicated"), subtitle: c ? `"${c.name}" added.` : undefined }); } catch (e) { console.warn("duplicate flow failed:", e); toast({ tone: "danger", title: t("messaging.flowsLibrary.couldnTDuplicateFlow", "Couldn't duplicate flow"), subtitle: t("messaging.flowsLibrary.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (f: SeedFlow) => { try { await store.remove(f.id); toast({ tone: "warn", title: t("messaging.flowsLibrary.flowDeleted", "Flow deleted") }); } catch (e) { console.warn("delete flow failed:", e); toast({ tone: "danger", title: t("messaging.flowsLibrary.couldnTDeleteFlow", "Couldn't delete flow"), subtitle: t("messaging.flowsLibrary.retryInAMoment", "Retry in a moment.") }); } };
  const actions = (f: SeedFlow) => ({
    onView: () => nav.go("messaging.flow", { id: f.id, view: 1 }),
    viewLabel: "Open",
    onEdit: canWrite ? () => nav.go("messaging.flow", { id: f.id }) : undefined,
    onDuplicate: canWrite ? () => dup(f) : undefined,
    moreHeader: f.name,
    moreItems: canWrite ? [{ icon: <Icon.trash size={14} />, label: t("messaging.flowsLibrary.deleteFlow", "Delete flow"), danger: true as const, onClick: () => del(f) }] : [],
  });

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "flows", counts)} activeTab="flows">
      <PageHeader
        title={t("messaging.flowsLibrary.messaging", "Messaging")}
        subtitle={t("messaging.flowsLibrary.communicationsFlowsMultiStepJourneysBuiltFromYourMessageLibrary", "Communications flows — multi-step journeys built from your message library.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {/* §2.2 (2026-07-26): AWAIT the create and navigate with the id the
              server returned — the old unawaited upsert navigated on a temp
              flow_<ts> id the cache then swapped, opening a blank editor; a
              rejected create now surfaces instead of an unhandled rejection. */}
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => { void (async () => { try { const saved = await store.upsert(store.newFlow()); nav.go("messaging.flow", { id: saved.id }); } catch (e) { console.warn("create flow failed:", e); toast({ tone: "danger", title: t("messaging.flowsLibrary.couldntCreateFlow", "Couldn't create the flow"), subtitle: t("messaging.flowsLibrary.nothingWasSaved", "Nothing was saved — try again.") }); } })(); }}>{t("messaging.flowsLibrary.newFlow", "New flow")}</Btn> : null}
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px" }}>
        {store.error ? (
          <div style={{ padding: "12px 14px", marginBottom: 14, background: T.red2, border: `1px solid ${T.red1}`, borderRadius: 8, display: "flex", gap: 10, alignItems: "center", fontSize: 13, color: T.n1 }}>
            <Icon.warn size={15} style={{ color: T.red1, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{store.error}</span>
            <Btn variant="secondary" size="sm" onClick={() => store.reload()}>{t("common.actions.retry", "Retry")}</Btn>
          </div>
        ) : null}
        <div style={{ margin: "0 -24px" }}><FilterBar>
          <Input placeholder={t("messaging.flowsLibrary.findAFlow", "Find a flow…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 260 }} />
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={flowSelectStyle}>
            <option value="all">{t("messaging.flowsLibrary.allEventTypes", "All event types")}</option>
            {kindOpts.map((k) => <option key={k} value={k}>{(FLOW_KIND_PILL[k] || { label: k }).label}</option>)}
          </select>
          <select value={trigFilter} onChange={(e) => setTrigFilter(e.target.value)} style={flowSelectStyle}>
            <option value="all">{t("messaging.flowsLibrary.allTriggers", "All triggers")}</option>
            {trigOpts.map((ev) => <option key={ev} value={ev}>{t("messaging.flowsLibrary.starts", "Starts:")}{" "}{flowTrigLabel(ev)}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: T.soft }}>{rows.length} {t("messaging.flowsLibrary.of", "of")}{" "}{all.length}</span>
        </FilterBar></div>
        {view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
            {rows.map((f) => {
              const st = flowStats(f); const kind = FLOW_KIND_PILL[f.eventKind] || { label: f.eventKind, tone: "primary" as const };
              const trigText = flowTrigLabel(f.trigger && f.trigger.event);
              return (
                <Card key={f.id} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden" }} onClick={() => nav.go("messaging.flow", { id: f.id })}>
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, minWidth: 0 }}>{f.name || "Untitled flow"}</div>
                      <Pill tone={kind.tone}>{kind.label}</Pill>
                    </div>
                    <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.45 }}>{f.desc}</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.p1, fontWeight: 600 }}>
                      <Icon.send size={12} /> {t("messaging.flowsLibrary.starts", "Starts:")}{" "}{trigText}
                    </div>
                    <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 14, fontSize: 12, color: T.soft }}>
                      <span><b style={{ color: T.n1 }}>{st.steps}</b> {t("messaging.flowsLibrary.stepsb7595e2a", "steps")}</span>
                      <span><b style={{ color: T.n1 }}>{st.messages}</b> {t("messaging.flowsLibrary.messagesf5cccfb7", "messages")}</span>
                      <span><b style={{ color: T.n1 }}>{st.decisions}</b> {t("messaging.flowsLibrary.decisions", "decisions")}</span>
                    </div>
                  </div>
                  <CardActionBar {...actions(f)} />
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0, marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={msgTh}>{t("messaging.flowsLibrary.flow", "Flow")}</th><th style={msgTh}>{t("messaging.flowsLibrary.eventType", "Event type")}</th><th style={msgTh}>{t("messaging.flowsLibrary.startsOn", "Starts on")}</th><th style={msgTh}>{t("messaging.flowsLibrary.steps", "Steps")}</th><th style={msgTh}>{t("messaging.flowsLibrary.messages", "Messages")}</th><th style={{ ...msgTh, width: 140 }}>{t("messaging.flowsLibrary.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f, i) => {
                  const st = flowStats(f); const kind = FLOW_KIND_PILL[f.eventKind] || { label: f.eventKind, tone: "primary" as const };
                  const trigText = flowTrigLabel(f.trigger && f.trigger.event);
                  return (
                    <tr key={f.id} onClick={() => nav.go("messaging.flow", { id: f.id })} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                      <td style={msgTd}><div style={{ fontWeight: 600, color: T.n1 }}>{f.name}</div><div style={{ fontSize: 11.5, color: T.soft }}>{f.desc}</div></td>
                      <td style={msgTd}><Pill tone={kind.tone}>{kind.label}</Pill></td>
                      <td style={{ ...msgTd, color: T.n2 }}>{trigText}</td>
                      <td style={{ ...msgTd, color: T.n1 }}>{st.steps}</td>
                      <td style={{ ...msgTd, color: T.n1 }}>{st.messages}</td>
                      <td style={msgTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(f)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
        <ExplainerFooter>
          <b>{t("messaging.flowsLibrary.functionalExamples", "Functional examples.")}</b> {t("messaging.flowsLibrary.theseFlowsAreWiredToYourMessagingLibraryOpenAnyOneTo", "These flows are wired to your Messaging library — open any one to see the journey on the canvas, edit steps, or swap messages. Triggers are the two real entry points:")}{" "}<b>{t("messaging.flowsLibrary.guestIsInvited", "Guest is invited")}</b> {t("messaging.flowsLibrary.and", "and")}{" "}<b>{t("messaging.flowsLibrary.guestRegisters", "Guest registers")}</b>.
        </ExplainerFooter>
      </div>
    </Screen>
  );
}
